/**
 * @ComponentFor MagazinePageViewModel
 */
import React from 'react';
import { styled } from '@glitz/react';
import { widthFromBreakpoint, Breakpoint } from '@avensia/scope';
import { ContentArea } from '@avensia/scope-episerver';
import { Article as BaseArticle } from 'Shared/PageLayout';
import MagazinePageViewModel from './MagazinePageViewModel.type';
import BaseImage from 'Shared/Image';
import { getSizes, getPresetFromWidth } from 'Shared/image-sizes';
import { Preset } from 'Shared/Image/Ratio';
import connect from 'Shared/connect';
import { pageMaxWidth, alpha, delta, pixelsToUnit, minMediumMediaQuery } from 'Shared/Style';
import Author from './MagazineAuthor';

type ConnectStateType = {
  currentBreakpoint: number;
};

type Props = MagazinePageViewModel & ConnectStateType;

const MagazinePage = (props: Props) => {
  const estimatedWidth = Math.min(
    widthFromBreakpoint(Math.min(props.currentBreakpoint, Breakpoint.Gigantic)),
    pageMaxWidth,
  );
  const preset = getPresetFromWidth(estimatedWidth, getSizes()) || Preset.Tiny;
  const src = props.imageUrl;

  return (
    <Article>
      <Image preset={preset} src={src.url} />
      <Content>
        <Title>{props.title}</Title>
        <ContentArea {...props.mainContent} />
        {(props.authorName || props.authorDescription || props.authorImageUrl) && (
          <Author
            authorName={props.authorName}
            authorDescription={props.authorDescription}
            authorImageUrl={props.authorImageUrl}
          />
        )}
      </Content>
    </Article>
  );
};

export default connect(
  (state): ConnectStateType => ({
    currentBreakpoint: state.currentBreakpoint,
  }),
)(MagazinePage);

const Article = styled(BaseArticle, {
  margin: {
    x: 'auto',
  },
  maxWidth: pixelsToUnit(1000),
  width: '100%',
  [minMediumMediaQuery]: {
    paddingTop: pixelsToUnit(25),
  },
});

const Image = styled(BaseImage, {
  display: 'block',
  width: '100%',
  objectFit: 'cover',
});

const Content = styled.div({
  margin: {
    y: 0,
    x: 'auto',
  },
  padding: {
    y: pixelsToUnit(15),
  },
  width: `calc(${100 - 2 * 4.6875}%)`, // 15px / 320px = 0.046875
});

const Title = styled.h1({
  fontSize: delta,
  lineHeight: 'normal',
  maxWidth: pixelsToUnit(600),
  margin: {
    y: 0,
    x: 'auto',
  },
  width: '100%',
  [minMediumMediaQuery]: {
    fontSize: alpha,
  },
});
